import { useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

const LIST_GAP_PX = 6;
const VIEWPORT_MARGIN_PX = 12;
const COMFORTABLE_LIST_PX = 260;

export type PopupAlign = 'start' | 'end';

const isRightToLeft = (anchor: HTMLElement): boolean => getComputedStyle(anchor).direction === 'rtl';

const horizontalEdge = (anchor: HTMLElement, box: DOMRect, align: PopupAlign): CSSProperties =>
  align === 'end' && !isRightToLeft(anchor) ? { right: window.innerWidth - box.right } : { left: box.left };

const placeUnder = (anchor: HTMLElement, align: PopupAlign): CSSProperties => {
  const box = anchor.getBoundingClientRect();
  const edge = horizontalEdge(anchor, box, align);
  const below = window.innerHeight - box.bottom - LIST_GAP_PX - VIEWPORT_MARGIN_PX;
  const above = box.top - LIST_GAP_PX - VIEWPORT_MARGIN_PX;
  const room = (pixels: number) => ({ '--popup-room': `${String(pixels)}px` }) as CSSProperties;
  if (below < COMFORTABLE_LIST_PX && above > below)
    return {
      bottom: window.innerHeight - box.top + LIST_GAP_PX,
      ...edge,
      width: box.width,
      ...room(above),
    };
  return { top: box.bottom + LIST_GAP_PX, ...edge, width: box.width, ...room(below) };
};

export const usePlacementUnder = (
  anchor: RefObject<HTMLElement | null>,
  open: boolean,
  align: PopupAlign = 'start',
): CSSProperties => {
  const [place, setPlace] = useState<CSSProperties>({});
  useLayoutEffect(() => {
    if (!open) return undefined;
    const follow = () => {
      if (anchor.current !== null) setPlace(placeUnder(anchor.current, align));
    };
    follow();
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [anchor, open, align]);
  return place;
};
